import React, { useState, useEffect, useCallback } from 'react';
import { Shield, X } from 'lucide-react';
import { API_BASE } from '../api';
import StatCard from './StatCard';

export default function AdminView({ token, username, clearChat }) {
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminStats, setAdminStats] = useState(null);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunde inte hämta konton.');
      if (!Array.isArray(data)) throw new Error('Oväntat svar från servern.');
      setAdminUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      alert(err.message);
    }
  }, [token]);

  const fetchAdminStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunde inte hämta statistik.');
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Oväntat svar från servern.');
      setAdminStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token]);

  const deleteUser = useCallback(async (userId, userUsername) => {
    if (!confirm(`Är du säker på att du vill ta bort användaren "${userUsername}"? Detta går inte att ångra.`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Kunde inte ta bort användaren.');
      setAdminUsers(prev => prev.filter(u => u.id !== userId));
      fetchAdminStats();
    } catch (err) {
      alert(err.message);
    }
  }, [token, fetchAdminStats]);

  useEffect(() => {
    fetchAdminUsers();
    fetchAdminStats();
  }, [fetchAdminUsers, fetchAdminStats]);

  return (
    <>
      <header className="library-header">
        <div className="library-icon"><Shield /></div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>Admin</h2>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Hantera användare och chatt</div>
        </div>
        <button onClick={clearChat} style={{
          background: 'rgba(255, 80, 80, 0.15)', color: '#ff6b6b',
          border: '1px solid rgba(255, 80, 80, 0.3)', borderRadius: 'var(--radius-full)',
          padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '13px',
          display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
        }}><X size={16} /> Töm chatten</button>
      </header>

      {adminStats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '14px', marginBottom: '32px', width: 'min(1160px, 100%)',
          marginLeft: 'auto', marginRight: 'auto'
        }}>
          <StatCard label="Totala konton" value={adminStats.totalUsers} />
          <StatCard label="Nya (7 dagar)" value={adminStats.newUsersLast7Days} highlight />
          <StatCard label="Meddelanden" value={adminStats.totalMessages} />
          <StatCard label="Spellistor" value={adminStats.totalPlaylists} />
        </div>
      )}

      <div style={{ width: 'min(1160px, 100%)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Användare</h3>
        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{adminUsers.length} konton</span>
      </div>
      <div className="list-header" style={{ gridTemplateColumns: '50px minmax(160px, 1.2fr) 80px 100px 100px 160px 140px 60px' }}>
        <span>ID</span><span>Användarnamn</span><span>Roll</span><span>Spellistor</span><span>Meddelanden</span><span>Skapad</span><span>Senast aktiv</span><span></span>
      </div>
      <div className="songs-list">
        {adminUsers.map(user => {
          const isNew = user.created_at && (Date.now() - new Date(user.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000;
          return (
            <div key={user.id} className={`song-list-item ${isNew ? 'new-user' : ''}`} style={{ gridTemplateColumns: '50px minmax(160px, 1.2fr) 80px 100px 100px 160px 140px 60px', cursor: 'default' }}>
              <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{user.id}</span>
              <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=random&color=fff&size=30`} alt={user.username} style={{ width: '30px', height: '30px', borderRadius: '4px' }} />
                {user.username}
                {isNew && <span style={{ background: 'rgba(80, 220, 120, 0.15)', color: '#50dc78', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.5px' }}>NY</span>}
              </span>
              <span>
                <span style={{
                  background: user.role === 'admin' ? 'rgba(118, 185, 255, 0.15)' : 'rgba(255, 255, 255, 0.06)',
                  color: user.role === 'admin' ? 'var(--primary)' : 'var(--text-muted)',
                  fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px'
                }}>{user.role === 'admin' ? 'Admin' : 'User'}</span>
              </span>
              <span>{user.playlist_count}</span>
              <span>{user.message_count}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                {user.created_at ? new Date(user.created_at).toLocaleDateString('sv-SE') : 'Okänt'}
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                {user.last_playback ? new Date(user.last_playback).toLocaleString('sv-SE') : 'Aldrig'}
              </span>
              <div style={{ textAlign: 'center' }}>
                {user.username !== username && (
                  <button onClick={() => deleteUser(user.id, user.username)} style={{
                    background: 'none', border: 'none', color: 'var(--text-muted)',
                    cursor: 'pointer', padding: '4px', borderRadius: '6px',
                    transition: 'all 0.2s', display: 'inline-flex'
                  }} title={`Ta bort ${user.username}`}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ff6b6b'; e.currentTarget.style.background = 'rgba(255,80,80,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
                  ><X size={16} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
